import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";
import { Devices, CircleNotch, DeviceMobile, Desktop, Prohibit, SignOut } from "@phosphor-icons/react";

export default function DevicesAccess() {
  const { csrf, logout } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/devices"); setData(r.data); } catch (_) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const revoke = async (deviceId) => {
    setBusy(true);
    try {
      await api.post(`/devices/${deviceId}/revoke`, {}, { headers: { "X-CSRF-Token": csrf } });
      toast.success("Device revoked", { description: "Its PIN and sessions are no longer valid." });
      await load();
    } catch { toast.error("Could not revoke device"); }
    setBusy(false);
  };

  const signOutAll = async () => {
    setBusy(true);
    try {
      await api.post("/auth/logout-all", {}, { headers: { "X-CSRF-Token": csrf } });
      window.location.href = "/login";
    } catch { toast.error("Could not sign out all devices"); setBusy(false); }
  };

  const fmt = (iso) => (iso ? formatDateTime(iso) : "—");

  return (
    <div className="max-w-4xl" data-testid="devices-page">
      <div className="flex items-center gap-2 eyebrow mb-3">
        <Devices size={14} weight="fill" className="text-primary" /> Security
      </div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="t-page-title">Devices and access</h1>
        <button
          data-testid="signout-all-button" onClick={signOutAll} disabled={busy}
          className="inline-flex items-center gap-2 border border-red-300 text-red-700 px-4 py-2 t-support font-semibold rounded-md hover:bg-red-50 disabled:opacity-60 transition-colors"
        >
          <SignOut size={15} weight="bold" /> Sign out all devices
        </button>
      </div>

      {!data ? (
        <div className="mt-10 flex items-center gap-2 text-muted-foreground t-support">
          <CircleNotch size={18} className="animate-spin text-primary" weight="bold" /> loading…
        </div>
      ) : (
        <>
          <p className="mt-3 t-support text-muted-foreground">
            {data.active_sessions} active session{data.active_sessions === 1 ? "" : "s"} on your account.
          </p>
          <div className="mt-5 border border-border bg-surface rounded-md divide-y divide-border" data-testid="devices-list">
            {data.devices.length === 0 && (
              <p className="p-6 t-support text-muted-foreground" data-testid="devices-empty">
                No registered mobile devices yet. Use <b>Get Mobile Access</b> to pair a phone.
              </p>
            )}
            {data.devices.map((d) => (
              <div key={d.device_id} data-testid={`device-row-${d.device_id}`}
                className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                <div className="flex items-center gap-3 min-w-0">
                  {d.platform === "Android" || d.platform === "iOS"
                    ? <DeviceMobile size={22} weight="duotone" className="text-primary shrink-0" />
                    : <Desktop size={22} weight="duotone" className="text-primary shrink-0" />}
                  <div className="min-w-0">
                    <p className="t-support font-semibold truncate">{d.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {d.platform} · {d.browser} · registered {fmt(d.first_registered_at)} · last used {fmt(d.last_used_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 border rounded ${
                    d.status === "active" ? "border-primary/40 text-primary" : "border-black/15 text-muted-foreground"}`}>
                    {d.status}
                  </span>
                  {d.status === "active" && (
                    <button
                      data-testid={`revoke-device-${d.device_id}`} onClick={() => revoke(d.device_id)} disabled={busy}
                      className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 t-support font-semibold rounded-md hover:bg-secondary disabled:opacity-60"
                    >
                      <Prohibit size={14} weight="bold" /> Revoke
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
